import { useState } from "react";
import { api, ApiError, type ConnectionStatus } from "../lib/api";
import { formatRelative } from "../lib/format";
import { useToast } from "./Toast";

interface Props {
  status: ConnectionStatus | null;
  onChanged: () => void;
}

/**
 * The Cloudreve connection. Authorising once here is what lets the service run
 * unattended: the backend keeps the grant refreshed from then on.
 */
export function ConnectionPanel({ status, onChanged }: Props) {
  const { notify } = useToast();
  const [isWorking, setIsWorking] = useState(false);

  // The return leg of this flow is handled by useCloudreveCallback at the
  // dashboard level, because the operator comes back into a fresh page load
  // with this panel closed.

  async function handleConnect() {
    setIsWorking(true);
    try {
      const { authorize_url } = await api.connection.authorize();
      // A popup keeps the dashboard state intact while consent happens.
      const popup = window.open(authorize_url, "cloudreve-authorize", "width=520,height=720");
      if (!popup) {
        window.location.href = authorize_url;
      }
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Could not start authorisation.", "error");
    } finally {
      setIsWorking(false);
    }
  }

  async function handleRefresh() {
    setIsWorking(true);
    try {
      await api.connection.refresh();
      notify("Token refreshed.", "success");
      onChanged();
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Refresh failed.", "error");
    } finally {
      setIsWorking(false);
    }
  }

  async function handleDisconnect() {
    if (!window.confirm("Disconnect from Cloudreve?\n\nUploads stop working until you authorise again.")) {
      return;
    }
    setIsWorking(true);
    try {
      await api.connection.disconnect();
      notify("Disconnected.", "info");
      onChanged();
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Could not disconnect.", "error");
    } finally {
      setIsWorking(false);
    }
  }

  const state = !status?.connected ? "down" : status.refresh_valid ? "live" : "warn";
  const stateLabel = !status?.connected
    ? "Not connected"
    : status.refresh_valid
      ? "Connected"
      : "Authorisation expired";

  return (
    <section className="panel">
      <header className="panel__head">
        <span className="label">Cloudreve</span>
        <span className="status" data-state={state}>
          {stateLabel}
        </span>
      </header>

      <div className="panel__body">
        {!status?.connected ? (
          <>
            <p className="field__hint">
              Authorise once. The service keeps the session refreshed from then on,
              so uploads keep working without you signing in again.
            </p>
            <button className="btn btn--primary" type="button" onClick={handleConnect} disabled={isWorking}>
              Authorise Cloudreve
            </button>
          </>
        ) : (
          <>
            <dl className="datalist">
              <dt>Account</dt>
              <dd>{status.account_label || "unknown"}</dd>
              <dt>Access</dt>
              <dd>renews {formatRelative(status.access_expires)}</dd>
              <dt>Session</dt>
              <dd>
                {status.refresh_valid
                  ? `valid until ${formatRelative(status.refresh_expires)}`
                  : "expired, authorise again"}
              </dd>
            </dl>

            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="btn" type="button" onClick={handleRefresh} disabled={isWorking}>
                Refresh now
              </button>
              <button className="btn btn--danger" type="button" onClick={handleDisconnect} disabled={isWorking}>
                Disconnect
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
